import { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { authApi, type AuthResponse, type ApiMenuTreeNode } from '../services/api'
import { clearAuth as clearStorageAuth } from '../utils/request'

const AUTH_STORAGE_KEY = 'asset_auth'

export interface UserRole {
  id: string
  name: string
  code: string
}

export interface User {
  id: string
  username: string
  name?: string
  email?: string
  nickname?: string
  phone?: string
  avatarUrl?: string | null
  roles?: UserRole[]
  /** 兼容旧接口：后端直接返回的 permissions */
  permissions?: string[]
}

export type MenuTreeNode = ApiMenuTreeNode

interface AuthState {
  token: string | null
  user: User | null
  /** 当前用户可见的权限菜单树（新接口 data.menuTree） */
  menuTree: MenuTreeNode[]
  /** 当前用户按钮权限 code 列表（新接口 data.buttonPermissions） */
  buttonPermissions: string[]
}

interface AuthContextValue extends AuthState {
  isAuthenticated: boolean
  /** 检查当前用户是否拥有指定按钮/菜单权限（ADMIN 角色拥有全部） */
  hasPermission: (code: string) => boolean
  /** 检查当前用户是否拥有指定角色 */
  hasRole: (code: string) => boolean
  login: (username: string, password: string) => Promise<{ success: boolean; message?: string }>
  register: (params: {
    username: string
    email: string
    password: string
  }) => Promise<{ success: boolean; message?: string }>
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

function loadStoredAuth(): AuthState {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY)
    if (!raw) return { token: null, user: null, menuTree: [], buttonPermissions: [] }
    const data = JSON.parse(raw) as AuthState
    if (data.token && data.user) {
      return {
        token: data.token,
        user: data.user,
        menuTree: Array.isArray(data.menuTree) ? data.menuTree : [],
        buttonPermissions: Array.isArray(data.buttonPermissions) ? data.buttonPermissions : [],
      }
    }
  } catch {
    // ignore
  }
  return { token: null, user: null, menuTree: [], buttonPermissions: [] }
}

function saveAuth(
  token: string,
  user: User,
  menuTree: MenuTreeNode[] = [],
  buttonPermissions: string[] = []
) {
  localStorage.setItem(
    AUTH_STORAGE_KEY,
    JSON.stringify({ token, user, menuTree, buttonPermissions })
  )
}

function normalizeUser(raw: any, fallback: { username: string; email?: string }): User {
  if (!raw) {
    return {
      id: String(Date.now()),
      username: fallback.username,
      email: fallback.email ?? fallback.username,
      nickname: fallback.username,
      roles: [],
      permissions: [],
    }
  }
  let roles: UserRole[] = []
  const rawRoles = raw.roles
  if (Array.isArray(rawRoles)) {
    roles = rawRoles.map((r: any) => {
      const code = (typeof r === 'object' ? r.code ?? r.role_code : String(r))?.toLowerCase() ?? ''
      return {
        id: typeof r === 'object' ? String(r.id ?? r.role_id ?? '') : '',
        name: typeof r === 'object' ? (r.name ?? r.role_name ?? code) : code,
        code,
      }
    })
  } else if (raw.role != null) {
    const roleCode = String(raw.role_code ?? raw.role ?? '').toLowerCase()
    if (roleCode) roles = [{ id: '', name: roleCode, code: roleCode }]
  }
  const permissions = Array.isArray(raw.permissions) ? raw.permissions : []
  return {
    id: raw.id != null ? String(raw.id) : String(Date.now()),
    username: raw.username ?? fallback.username,
    name: raw.name,
    email: raw.email ?? fallback.email,
    nickname: raw.nickname ?? raw.name ?? raw.username ?? fallback.username,
    phone: raw.phone,
    avatarUrl: raw.avatarUrl,
    roles,
    permissions,
  }
}

function getTokenFromResponse(res: AuthResponse): string | null {
  const data = (res as any)?.data
  return data?.token ?? res?.token ?? data?.access_token ?? res?.access_token ?? null
}

function getUserFromResponse(res: AuthResponse, fallback: { username: string; email?: string }): User {
  const raw = (res as any)?.data?.user ?? (res as any)?.user
  return normalizeUser(raw, fallback)
}

function getMenuTreeFromResponse(res: AuthResponse): MenuTreeNode[] {
  const tree = (res as any)?.data?.menuTree
  return Array.isArray(tree) ? tree : []
}

function getButtonPermissionsFromResponse(res: AuthResponse): string[] {
  const list = (res as any)?.data?.buttonPermissions
  return Array.isArray(list) ? list : []
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>(loadStoredAuth)

  useEffect(() => {
    const stored = loadStoredAuth()
    setState(stored)
  }, [])

  const login = useCallback(async (username: string, password: string) => {
    if (!username.trim() || !password.trim()) {
      return { success: false, message: '请输入用户名和密码' }
    }
    try {
      const res = (await authApi.login({ username: username.trim(), password })) as AuthResponse
      const token = getTokenFromResponse(res)
      if (!token) {
        return { success: false, message: '接口未返回 token' }
      }
      const user = getUserFromResponse(res, { username: username.trim(), email: '' })
      const menuTree = getMenuTreeFromResponse(res)
      const buttonPermissions = getButtonPermissionsFromResponse(res)
      saveAuth(token, user, menuTree, buttonPermissions)
      setState({ token, user, menuTree, buttonPermissions })
      return { success: true }
    } catch (err: unknown) {
      const e = err as {
        friendlyMessage?: string
        message?: string
        response?: { data?: { detail?: string; message?: string } }
      }
      const data = e?.response?.data
      const msg =
        e?.friendlyMessage ??
        data?.detail ??
        data?.message ??
        e?.message ??
        '登录失败'
      return { success: false, message: typeof msg === 'string' ? msg : '登录失败' }
    }
  }, [])

  const register = useCallback(
    async (params: { username: string; email: string; password: string }) => {
      const { username, email, password } = params
      if (!username.trim()) return { success: false, message: '请输入用户名' }
      if (!email.trim()) return { success: false, message: '请输入邮箱' }
      if (!password.trim()) return { success: false, message: '请输入密码' }
      if (password.length < 6) return { success: false, message: '密码至少 6 位' }
      try {
        const res = (await authApi.register({
          username: username.trim(),
          email: email.trim(),
          password,
        })) as AuthResponse
        const token = getTokenFromResponse(res)
        if (!token) {
          return { success: false, message: '接口未返回 token' }
        }
        const user = getUserFromResponse(res, {
          username: username.trim(),
          email: email.trim(),
        })
        const menuTree = getMenuTreeFromResponse(res)
        const buttonPermissions = getButtonPermissionsFromResponse(res)
        saveAuth(token, user, menuTree, buttonPermissions)
        setState({ token, user, menuTree, buttonPermissions })
        return { success: true }
      } catch (err: unknown) {
        const e = err as {
          friendlyMessage?: string
          message?: string
          response?: { data?: { detail?: string; message?: string } }
        }
        const data = e?.response?.data
        const msg =
          e?.friendlyMessage ??
          data?.detail ??
          data?.message ??
          e?.message ??
          '注册失败'
        return { success: false, message: typeof msg === 'string' ? msg : '注册失败' }
      }
    },
    []
  )

  const logout = useCallback(() => {
    clearStorageAuth()
    setState({ token: null, user: null, menuTree: [], buttonPermissions: [] })
  }, [])

  const hasRole = useCallback(
    (code: string) => {
      const roles = state.user?.roles ?? []
      const c = (code || '').toLowerCase()
      return roles.some((r) => (r.code || '').toLowerCase() === c)
    },
    [state.user?.roles]
  )

  const hasPermission = useCallback(
    (code: string) => {
      const roles = state.user?.roles ?? []
      const buttonPerms = state.buttonPermissions ?? []
      const legacyPerms = state.user?.permissions ?? []
      const isAdmin =
        roles.some((r) => (r.code || '').toLowerCase() === 'admin') ||
        (state.user?.username || '').toLowerCase() === 'admin'
      if (isAdmin) return true
      if (buttonPerms.length > 0) return buttonPerms.includes(code)
      if (legacyPerms.includes(code)) return true
      if (code === 'users:manage') return legacyPerms.some((p) => String(p).startsWith('users:'))
      if (code === 'roles:manage') return legacyPerms.some((p) => String(p).startsWith('roles:'))
      if (String(code).startsWith('users:'))
        return legacyPerms.includes('users:manage') || legacyPerms.includes(code)
      if (String(code).startsWith('roles:'))
        return legacyPerms.includes('roles:manage') || legacyPerms.includes(code)
      if (roles.length === 0 && legacyPerms.length === 0) return true
      return false
    },
    [state.user?.roles, state.user?.permissions, state.buttonPermissions]
  )

  const value: AuthContextValue = {
    ...state,
    isAuthenticated: !!state.token,
    hasPermission,
    hasRole,
    login,
    register,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
